import { create } from 'zustand';
import { getToken } from '../session/store';

// ─── LTP WebSocket Store ──────────────────────────────────────────────────────
// Singleton app-wide WebSocket for live LTP streaming.
// Components call subscribe(symbols) / unsubscribe(symbols).
// LTP map: { "NSE:NIFTY50-INDEX": 23130.5, "NSE:SBIN-EQ": 1018.0, ... }
//
// Same socket also carries an additive `type:"quote"` message (backend-emitted, no separate
// subscribe action needed) with richer per-symbol data (change_percent, prev_close, bid/ask,
// volume, etc.) — stored separately in `quotes`, never merged into `ltp`, so existing `ltp`
// consumers are completely unaffected.
// Quotes map: { "NSE:SBIN-EQ": { ltp, prev_close, change_percent, ... } }
//
// Resilience: the socket auto-reconnects + resubscribes all active (ref-counted) symbols on
// close, on wake events (visibilitychange/focus/online/pageshow), and when a half-open socket
// goes stale. Last known LTP/quote is NEVER cleared on disconnect — values are preserved while
// reconnecting until a fresh tick arrives.

const WS_RECONNECT_MS = 3000;
const WS_HEARTBEAT_MS = 30000;
// Half-open detection: the socket reads OPEN but no data is flowing. Only treated as stale once
// real data has been seen (_lastDataAt > 0) AND the connection has had time to settle
// (_connectedAt grace) — so a market with no ticks never triggers a reconnect loop.
const RESUME_STALE_MS = 8000;      // checked on wake events
const HEARTBEAT_STALE_MS = 45000;  // checked by the periodic heartbeat

function buildWsUrl() {
  const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${location.host}/api/ws/ltp`;
}

let _socket = null;
let _reconnectTimer = null;
let _heartbeatTimer = null;
let _refCounts = new Map(); // symbol → count of active subscribers
let _lastDataAt = 0;        // ts of the last real LTP message (0 = never received data)
let _connectedAt = 0;       // ts of the last socket open
let _listenersInstalled = false;

export const useLtpStore = create((set, get) => ({
  ltp: {},         // { symbol: price }
  quotes: {},      // { symbol: { ltp, prev_close, change_percent, ... } }
  connected: false,

  // ── Subscribe: increment ref count, send to WS ───────────────────────────
  subscribe: (symbols) => {
    if (!symbols || symbols.length === 0) return;
    const newSymbols = [];
    for (const sym of symbols) {
      const count = _refCounts.get(sym) || 0;
      _refCounts.set(sym, count + 1);
      if (count === 0) newSymbols.push(sym);
    }
    _ensureConnected(set, get);
    if (newSymbols.length > 0 && _socket?.readyState === WebSocket.OPEN) {
      _socket.send(JSON.stringify({ action: 'subscribe', symbols: newSymbols }));
    }
  },

  // ── Unsubscribe: decrement ref count, remove when 0 ──────────────────────
  unsubscribe: (symbols) => {
    if (!symbols || symbols.length === 0) return;
    const removeSymbols = [];
    for (const sym of symbols) {
      const count = _refCounts.get(sym) || 0;
      if (count <= 1) {
        _refCounts.delete(sym);
        removeSymbols.push(sym);
      } else {
        _refCounts.set(sym, count - 1);
      }
    }
    if (removeSymbols.length > 0 && _socket?.readyState === WebSocket.OPEN) {
      _socket.send(JSON.stringify({ action: 'unsubscribe', symbols: removeSymbols }));
    }
    // If no subscribers left, close the socket. Last known LTP values are NOT cleared.
    if (_refCounts.size === 0) {
      _disconnect(set);
    }
  },

  // ── Get LTP for a specific symbol ─────────────────────────────────────────
  getLtp: (symbol) => get().ltp[symbol] ?? null,

  // ── Get the full quote object for a specific symbol (null if none yet) ──
  getQuote: (symbol) => get().quotes[symbol] ?? null,
}));

// ─── Internal: connection management ──────────────────────────────────────────

function _now() {
  return Date.now();
}

// A socket is "stale" only if it's OPEN, we've actually received data before, none has arrived for
// `thresholdMs`, and the connection has been up at least `thresholdMs` (grace against tight loops).
function _isStale(thresholdMs) {
  return (
    _lastDataAt > 0 &&
    _now() - _lastDataAt > thresholdMs &&
    _now() - _connectedAt > thresholdMs
  );
}

function _ensureConnected(set, get) {
  _installWakeListeners(set, get);
  if (_socket && (_socket.readyState === WebSocket.OPEN || _socket.readyState === WebSocket.CONNECTING)) {
    return;
  }
  _connect(set, get);
}

function _connect(set, get) {
  _clearTimers();
  const token = getToken();
  const url = token ? `${buildWsUrl()}?token=${encodeURIComponent(token)}` : buildWsUrl();

  try {
    _socket = new WebSocket(url);
  } catch {
    _scheduleReconnect(set, get);
    return;
  }

  _socket.onopen = () => {
    _connectedAt = _now();
    set({ connected: true });
    // Re-subscribe all active symbols on (re)connect.
    const allSymbols = Array.from(_refCounts.keys());
    if (allSymbols.length > 0) {
      _socket.send(JSON.stringify({ action: 'subscribe', symbols: allSymbols }));
    }
    _startHeartbeat(set, get);
  };

  _socket.onmessage = (event) => {
    try {
      const msg = JSON.parse(event.data);
      if (msg.type === 'ltp' && msg.data) {
        _lastDataAt = _now();
        set((state) => {
          const prev = state.ltp;
          let changed = false;
          for (const sym in msg.data) {
            if (prev[sym] !== msg.data[sym]) { changed = true; break; }
          }
          if (!changed) return state;
          return { ltp: { ...prev, ...msg.data } };
        });
      } else if (msg.type === 'quote' && msg.data) {
        // Additive — per-symbol objects (not primitives), so always merge rather than
        // reusing the ltp branch's primitive !== shortcut.
        _lastDataAt = _now();
        set((state) => ({ quotes: { ...state.quotes, ...msg.data } }));
      }
    } catch { /* ignore malformed messages */ }
  };

  _socket.onclose = () => {
    set({ connected: false });
    _clearTimers();
    // Only reconnect if there are still active subscribers. ltp cache is preserved.
    if (_refCounts.size > 0) {
      _scheduleReconnect(set, get);
    }
  };

  _socket.onerror = () => {
    // onclose will fire after onerror — reconnect handled there
  };
}

// Tear down the current socket (without triggering its onclose-driven reconnect) and connect fresh.
// Used to recover a half-open / stale socket on wake or heartbeat detection.
function _forceReconnect(set, get) {
  if (_socket) {
    try {
      _socket.onopen = null;
      _socket.onmessage = null;
      _socket.onclose = null;
      _socket.onerror = null;
      _socket.close();
    } catch { /* ignore */ }
    _socket = null;
  }
  _clearTimers();
  _connect(set, get);
}

function _disconnect(set) {
  _clearTimers();
  if (_socket) {
    _socket.onclose = null; // prevent reconnect
    _socket.close();
    _socket = null;
  }
  set({ connected: false });
}

function _scheduleReconnect(set, get) {
  if (_reconnectTimer) return;
  _reconnectTimer = setTimeout(() => {
    _reconnectTimer = null;
    if (_refCounts.size > 0) _connect(set, get);
  }, WS_RECONNECT_MS);
}

function _startHeartbeat(set, get) {
  _heartbeatTimer = setInterval(() => {
    if (_socket?.readyState === WebSocket.OPEN) {
      _socket.send(JSON.stringify({ action: 'ping' }));
      // Recover a half-open socket even when no wake event fires (long-open focused tab).
      if (_refCounts.size > 0 && _isStale(HEARTBEAT_STALE_MS)) {
        _forceReconnect(set, get);
      }
    }
  }, WS_HEARTBEAT_MS);
}

function _clearTimers() {
  if (_reconnectTimer) { clearTimeout(_reconnectTimer); _reconnectTimer = null; }
  if (_heartbeatTimer) { clearInterval(_heartbeatTimer); _heartbeatTimer = null; }
}

// Wake/resume recovery: reconnect a closed/stale socket, or nudge a healthy one to resubscribe.
function _resumeIfNeeded(set, get) {
  if (_refCounts.size === 0) return;
  const rs = _socket?.readyState;
  if (rs === WebSocket.CONNECTING) return;            // already coming up
  if (rs !== WebSocket.OPEN) { _forceReconnect(set, get); return; } // closed/closing/null → reconnect
  if (_isStale(RESUME_STALE_MS)) { _forceReconnect(set, get); return; } // half-open → reconnect
  // Healthy & open → re-send a de-duped subscribe for all active symbols (recovers a dropped sub).
  const allSymbols = Array.from(_refCounts.keys());
  if (allSymbols.length > 0) {
    try { _socket.send(JSON.stringify({ action: 'subscribe', symbols: allSymbols })); } catch { /* ignore */ }
  }
}

// Installed once for the singleton's lifetime (guarded). Handlers no-op when there are no subs.
function _installWakeListeners(set, get) {
  if (_listenersInstalled || typeof window === 'undefined') return;
  _listenersInstalled = true;
  const onResume = () => _resumeIfNeeded(set, get);
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') onResume();
    });
  }
  window.addEventListener('focus', onResume);
  window.addEventListener('online', onResume);
  window.addEventListener('pageshow', onResume);
}
